/**
 * StepBrowse — Looks up the browse component from registry,
 * renders with Suspense (lazy-loaded), or shows manual input fallback.
 */

import { Suspense } from 'react';
import { useI18n } from '@/i18n';
import { Skeleton } from '@/components/ui';
import { getBrowseComponent } from '../browse/registry';
import type { BrowseSelection } from '../browse/types';
import type { EnrichedProviderManifest } from '../../types';

interface StepBrowseProps {
  manifest: EnrichedProviderManifest;
  credentialToken: string | null;
  installationId: number | null;
  onSelect: (selection: BrowseSelection) => void;
}

export function StepBrowse({
  manifest,
  credentialToken,
  installationId,
  onSelect,
}: StepBrowseProps) {
  const { t } = useI18n();
  const BrowseComponent = getBrowseComponent(manifest.provider_type);

  if (!BrowseComponent) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
        <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          {t('datasources.wizard.browse.manualTitle')}
        </p>
        <p className="text-sm">
          {t('datasources.wizard.browse.manualHint')}
        </p>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="space-y-3 max-w-4xl mx-auto">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      }
    >
      <BrowseComponent
        manifest={manifest}
        credentialToken={credentialToken}
        installationId={installationId}
        onSelect={onSelect}
      />
    </Suspense>
  );
}
